import React, { useContext, useMemo } from 'react';
import HelmetWrapper from 'components/HelmetWrapper';
import { BUYING_PORTFOLIO_ROUTE, SELLING_PORTFOLIO_ROUTE } from 'data/global';
import LinkToIcon from 'icons/LinkTo.icon';
import { Listing } from 'models/Listing';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import utils, { getIsListingOwner, getResolveUrl } from 'utils';

export default function useBusiness(listing: Listing) {
  const {
    isEnquired,
    dealRoomId,
    listingEnquiryId,
    userId,
    isCoOwner,
    listingTitle,
    businessDescription,
    gallery,
    industry,
    slugName,
    type,
  } = listing || {};

  const { currentUser, isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const isListingOwner = getIsListingOwner(currentUser?._id, userId, isCoOwner);

  // It was explicitly requested that Portfolio button should be shown for owners and boss-mode only
  const canGoToPortfolio = listing && (isSuperAdmin || isListingOwner);

  const HeadingNavigation = useMemo(() => {
    return (
      <nav>
        {canGoToPortfolio && (
          <Link
            to={`${
              isEnquired ? BUYING_PORTFOLIO_ROUTE : SELLING_PORTFOLIO_ROUTE
            }/${isEnquired ? listingEnquiryId : dealRoomId}`}
          >
            <LinkToIcon />
            {t('viewInMyPortfolio')}
          </Link>
        )}
      </nav>
    );
  }, [canGoToPortfolio, dealRoomId, isEnquired, listingEnquiryId, t]);

  const HelmetContent = useMemo(() => {
    if (!listing) return <></>;

    const title = `${listingTitle} | Resolve`;
    const description = businessDescription.slice(0, 150);
    const image = gallery?.[0]?.url;
    const url = getResolveUrl(
      utils.composeBusinessUrl(industry, slugName, type),
    );

    return (
      <HelmetWrapper
        title={title}
        description={description}
        url={url}
        facebookImg={image}
        twitterImg={image}
      />
    );
  }, [
    businessDescription,
    gallery,
    industry,
    listing,
    listingTitle,
    slugName,
    type,
  ]);

  return { HelmetContent, HeadingNavigation };
}
